<template>
  <div id="wrapper" ref="wrapperRef">
    <nav class="navbar navbar-dark fixed-top navbar-expand-md" :style="{'--bs-navbar-bg': navbarBackgroundColor, backgroundColor: navbarBackgroundColor}" role="navigation">
      <div class="container-fluid">
        <div class="top-section">
          <div class="header">
            <button
              type="button"
              class="navbar-toggler d-md-none"
              @click.stop="toggleNav"
              aria-label="Toggle navigation"
            >
              <span class="navbar-toggler-icon"></span>
            </button>
            <span class="navbar-brand mb-0">Admin Portal <span class="env-name">{{'(' + environmentName + ')'}}</span></span>
            <div class="d-md-none">
              <UserDropdown />
            </div>
          </div>
          <MainHeader v-if="isLoggedIn" />
        </div>
        <div class="side-section" v-show="navOpen || isDesktop">
          <SectionMenu v-if="isLoggedIn" />
        </div>
      </div>
    </nav>

    <div id="page-wrapper">
      <div class="container-fluid">
        <div class="row" id="main">
          <MessageContainer />
          <button class="login-button btn btn-primary" v-if="!isLoggedIn" @click="login">
            Login
          </button>

          <div v-if="isLoggedIn">
            <Login v-if="false" />
            <button v-if="showHistoryButton" @click="showHistoryRecords" class="btn btn-primary float-end history-button">History</button>
            <router-view />
            <RestoreDataModal />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Login from './Login'
import MainHeader from './MainHeader'
import MessageContainer from './MessageContainer'
import identityHandler from '@/core/identity-handler'
import SectionMenu from './SectionMenu'
import RestoreDataModal from './RestoreDataModal'
import { routesNames, sectionsNames } from '@/core/constants'
import UserDropdown from './UserDropdown.vue'

export default {
  created() {
    identityHandler.getProfile().then(response => {
      if (!response) {
        return
      }
      this.$store.commit('setLogin', {
        username: response.name,
        tenant: response.tenant
      })
    })

    identityHandler.isAdmin().then(response => {
      if (!response) {
        return
      }
      this.$store.commit('setIsAdmin', response)
    })
  },
  computed: {
    ...mapGetters(['currentSection', 'isLoggedIn', 'isAdmin', 'auditLogSelected']),
    showHistoryButton() {
      return this.currentSection && this.$route.name === routesNames.valueEditor
    },
    environmentName() {
      return window.runtimeConfig.environmentName
    },
    navbarBackgroundColor() {
      return window.runtimeConfig.navbarBackgroundColor
    }
  },
  data() {
    return {
      navOpen: false,
      isDesktop: window.innerWidth >= 768
    }
  },
  methods: {
    login() {
      this.$store.commit('login')
    },
    showHistoryRecords() {
      this.$router.push({name: 'History'})
    },
    toggleNav() {
      this.navOpen = !this.navOpen
    },
    closeNav(e) {
      if (this.$refs.wrapperRef && !this.$refs.wrapperRef.contains(e.target)) {
        this.navOpen = false
      }
    },
    handleResize() {
      this.isDesktop = window.innerWidth >= 768
      if (this.isDesktop) this.navOpen = false
    }
  },
  mounted() {
    document.addEventListener('click', this.closeNav)
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeNav)
    window.removeEventListener('resize', this.handleResize)
  },
  watch: {
    $route() {
      this.navOpen = false
    },
    currentSection(val, oldVal) {
      this.navOpen = false
      if (val.displayName === sectionsNames.auditLogs) {
        if (this.$route.name !== routesNames.auditLogs) this.$router.push({name: 'AuditLogs'})
        return
      }

      if (this.auditLogSelected) {
        this.$router.push({name: 'History'})
        this.$store.commit('clearAuditLogSelected')
        return
      }

      if (this.$route.name !== routesNames.valueEditor) this.$router.push({name: 'ValueEditor'})
    }
  },
  components: {
    UserDropdown,
    Login,
    MainHeader,
    MessageContainer,
    SectionMenu,
    RestoreDataModal
  }
}
</script>

<style>
:root {
  --navbar-height: 60px;
  --navbar-height-tablet: 90px;
  --navbar-height-mobile: 150px;
  --sidebar-width: 300px;
  --sidebar-minwidth: 280px;
  [data-bs-theme="dark"] {
    --bs-primary: #337ab7;
    --bs-primary-rgb: 51, 122, 183;
  }
  [data-bs-theme="light"] {
    --bs-primary: #337ab7;
    --bs-primary-rgb: 51, 122, 183;
  }
}

.list-group {
  --bs-list-group-active-border-color: var(--bs-primary);
}

[data-bs-theme="light"] {
  .list-group {
    --bs-list-group-border-color: #b8c4cf;
  }

  .btn-secondary {
    --bs-btn-border-color: #b8c4cf;
  }
}

.btn-primary {
  --bs-btn-bg: #337ab7;
  --bs-btn-border-color: #337ab7;
  --bs-btn-hover-bg: #204d74;
  --bs-btn-hover-border-color: #204d74;
}

body {
  padding-top: var(--navbar-height);
}

html {
  scrollbar-gutter: stable;
}
html::-webkit-scrollbar-track {
  background: var(--bs-tertiary-bg); /* Color of the tracking area */
}

#wrapper {
  padding-left: 0;
}

#page-wrapper {
  width: 100%;
  padding: 5px;
}

#page-wrapper .container-fluid {
  padding: 5px;
}

.navbar {
  height: var(--navbar-height-mobile);
  border-bottom: 1px solid rgba(0, 0, 0, 0.6);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);

  .container-fluid {
    height: 100%;
    flex-direction: column;
  }
}

@media (max-width: 768px) {
  .header {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-top: 0;
  }
}

@media (min-width:768px) {
  #wrapper {
    padding-left: var(--sidebar-width);
  }

  #page-wrapper {
    padding: 15px 5px;
  }

  .navbar {
    height: var(--navbar-height-tablet);
  }
}

@media (min-width: 1200px) {
  .navbar {
    height: var(--navbar-height);

    .container-fluid {
      flex-direction: row;
      flex-wrap: wrap;
    }
  }
}

.top-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  width: 100%;

  @media (min-width: 1200px) {
    flex-direction: row;
  }
}

.top-nav {
  display: flex;
  flex-direction: row;
  padding: 0;
  margin: 0;
  list-style: none;
  align-items: center;
  gap: 10px;
}

.top-nav > li {
  display: flex;
  align-items: center;
}

.top-nav > li > a {
  display: flex;
  align-items: center;
  min-height: var(--navbar-height);
  color: #fff;
  padding: 0 12px;
  text-decoration: none;
}

.top-nav > li.show > .dropdown-menu {
  position: absolute;
  margin-top: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  background-color: var(--bs-secondary-bg);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.175);
}

.top-nav > li.show > .dropdown-menu > li > a {
  white-space: normal;
}

.side-nav {
  top: var(--navbar-height-mobile);
}

@media (min-width: 768px) {
  .side-nav {
    top: var(--navbar-height-tablet);
  }
}

@media (min-width: 1200px) {
  .side-nav {
    top: var(--navbar-height);
  }
}

@media (min-width: 768px) {
  .side-nav {
    position: fixed;
    left: 0;
    max-width: var(--sidebar-width);
    min-width: var(--sidebar-minwidth);
    border: none;
    border-radius: 0;
    border-top: 1px rgba(0, 0, 0, 0.5) solid;
    border-right: 1px solid rgba(0, 0, 0, 0.6);
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.45);
    overflow-y: auto;
    overflow-x: hidden;
    background-color: #1a1d20;
    bottom: 0;
    padding-bottom: 40px;
  }

  ul.side-nav {
    flex-wrap: nowrap;
  }

  .side-nav > li > a {
    max-width: var(--sidebar-width);
    min-width: var(--sidebar-minwidth);
    border-bottom: 1px rgba(0, 0, 0, 0.3) solid;
  }
}

.side-nav li > ul {
  padding: 0;
  list-style-type: none;
  border-bottom: 1px rgba(0, 0, 0, 0.3) solid;
}

.side-nav li > ul > li > ul {
  padding-left: 10px;
  list-style-type: none;
  border-bottom: 1px rgba(0, 0, 0, 0.3) solid;
}

.side-nav li > ul > li > a {
  display: block;
  padding: 8px 15px 8px 38px;
  text-decoration: none;
  color: #fff;
}

.side-nav > li > ul > li > a:hover {
  color: #fff;
}

.side-nav li a:hover {
  background-color: #343a40;
}

.side-nav li.active {
  background-color: var(--bs-primary)!important;
}

.navbar .nav > li > a > .badge {
  border-radius: 50%;
  position: absolute;
  top: 14px;
  right: 6px;
  font-size: 10px;
  font-weight: normal;
  min-width: 15px;
  min-height: 15px;
  line-height: 1em;
  text-align: center;
  padding: 2px;
}

.navbar .nav > li > a:hover > .badge {
  top: 10px;
}

.navbar-brand {
  font-size: 20px;
  padding: 0 20px;
}

.navbar-brand .env-name {
  font-size: 14px;
}

.login-button {
  margin: 0 auto;
  display: block;
  max-width: 100px;
}

.btn-group .btn {
  background-color: var(--bs-body-bg);
  color: var(--bs-body-color);
}

.btn-group .btn:hover:not(:disabled) {
  background-color: var(--bs-tertiary-bg);
}

.btn-group .btn:active:not(:disabled) {
  background-color: var(--bs-secondary-bg);
  color: var(--bs-body-color);
}

.btn-group .btn:disabled {
  opacity: 0.35;
  color: var(--bs-secondary-color);
  cursor: not-allowed;
}

.history-button {
  position: absolute;
  right: 10px;
  margin-top: 5px;
  z-index: 1001;
}

@media (max-width: 768px) {
  .h1 {
    margin-top: 10px;
    margin-bottom: 0;
  }

  .history-button {
    margin-right: 10px;
  }
}

.side-section {
  position: absolute;
  top: var(--navbar-height-mobile);
  left: 0;
  width: 100%;
  background-color: #1a1d20;
  z-index: 1040;
  max-height: calc(100vh - var(--navbar-height-mobile));
  overflow-y: auto;
}

.side-nav {
  top: var(--navbar-height-mobile);
}

@media (min-width: 768px) {
  .side-section {
    position: static;
    background-color: transparent;
    max-height: none;
    overflow: visible;
    display: block !important;
  }

  .side-nav {
    top: var(--navbar-height-tablet);
    position: fixed;
    left: 0;
    max-width: var(--sidebar-width);
    min-width: var(--sidebar-minwidth);
    border: none;
    border-radius: 0;
    border-top: 1px rgba(0, 0, 0, 0.5) solid;
    border-right: 1px solid rgba(0, 0, 0, 0.6);
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.45);
    overflow-y: auto;
    overflow-x: hidden;
    background-color: #1a1d20;
    bottom: 0;
    padding-bottom: 40px;
  }

  ul.side-nav {
    flex-wrap: nowrap;
  }

  .side-nav > li > a {
    max-width: var(--sidebar-width);
    min-width: var(--sidebar-minwidth);
    border-bottom: 1px rgba(0, 0, 0, 0.3) solid;
  }
}

@media (min-width: 1200px) {
  .side-nav {
    top: var(--navbar-height);
  }
}

</style>



// WEBPACK FOOTER //
// src/components/Home.vue